/**
 * Shared Copilot SSE event semantics (Phase 6).
 *
 * Both the Redux-saga drawer path and the /copilot workspace path call
 * ``aiStreamPost`` via ``vertex360CopilotApi.sendMessageStream``. This module
 * is the single interpreter for mid-stream events so clarify / token / complete
 * / error behaviour cannot drift between the two UIs.
 *
 * Hosts apply returned ``effects`` (Redux puts or workspace dispatches).
 */

import { normalizeCopilotDegradedPayload } from "./copilotDegradedMode";
import { normalizeClarifyStreamRender } from "./vertex360CopilotChatUtils";

/** @typedef {'conversationId'|'userMessageId'|'assistantMessageId'|'content'|'render'|'sources'|'filters'|'degraded'|'unbusyClarify'|'unbusyComplete'} CopilotStreamOp */

/**
 * Map complete-event data → render/sources/suggestions (clarify-normalized).
 * @param {object|null|undefined} data
 */
export function mapCopilotStreamCompleteData(data) {
  let render = data?.render || undefined;
  if (render?.type === "clarify") {
    render = normalizeClarifyStreamRender({ render }) || render;
  }
  return {
    render,
    sources:
      Array.isArray(data?.sources) && data.sources.length
        ? data.sources
        : undefined,
    suggestions: Array.isArray(data?.suggestions)
      ? data.suggestions
      : undefined,
  };
}

/**
 * Map complete-event filter fields.
 * @param {object|null|undefined} data
 */
export function mapCopilotStreamFilters(data) {
  const hasFilters = Object.prototype.hasOwnProperty.call(
    data || {},
    "active_filters"
  );
  const hasSummary = Object.prototype.hasOwnProperty.call(
    data || {},
    "context_summary"
  );
  if (!hasFilters && !hasSummary) {
    return null;
  }
  return {
    activeFilters:
      data?.active_filters && typeof data.active_filters === "object"
        ? data.active_filters
        : null,
    contextSummary:
      typeof data?.context_summary === "string" && data.context_summary.trim()
        ? data.context_summary.trim()
        : null,
  };
}

/**
 * Resolve clarify/render payload to a normalized render object.
 * @param {object} payload
 */
export function resolveCopilotStreamRender(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (payload.type === "clarify") {
    return normalizeClarifyStreamRender(payload);
  }
  if (payload.type === "render") {
    if (payload.render?.type === "clarify") {
      return normalizeClarifyStreamRender(payload);
    }
    return payload.render || null;
  }
  return null;
}

/**
 * Create a mutable session that interprets SSE events into host effects.
 *
 * @param {{ assistantMessageId?: string|null, conversationId?: string|null, userMessageId?: string|null }} [initial]
 */
export function createCopilotStreamSession(initial = {}) {
  let content = "";
  let assistantMessageId = initial.assistantMessageId
    ? String(initial.assistantMessageId)
    : null;
  let conversationId = initial.conversationId
    ? String(initial.conversationId)
    : null;
  const userMessageId = initial.userMessageId
    ? String(initial.userMessageId)
    : null;

  const push = (effects, effect) => {
    if (effect) effects.push(effect);
  };

  /**
   * Interpret one SSE JSON event.
   * @param {object} payload
   * @returns {{ effects: object[], error?: Error }}
   */
  function handleEvent(payload) {
    const effects = [];
    if (!payload || typeof payload !== "object") {
      return { effects };
    }

    const type = payload.type;

    if (
      (type === "meta" || type === "complete") &&
      payload.conversation_id
    ) {
      const nextId = String(payload.conversation_id);
      if (nextId !== conversationId) {
        push(effects, {
          op: "conversationId",
          conversationId: nextId,
          previousConversationId: conversationId,
        });
        conversationId = nextId;
      }
    }

    if (type === "meta") {
      if (payload.message_id && userMessageId) {
        push(effects, {
          op: "userMessageId",
          messageId: String(payload.message_id),
          previousMessageId: userMessageId,
        });
      }
      return { effects };
    }

    if (type === "token" && payload.content) {
      const wasEmpty = content.length === 0;
      content += payload.content;
      push(effects, {
        op: "content",
        content,
        clearTyping: wasEmpty,
      });
      return { effects };
    }

    if (type === "clarify" || type === "render") {
      if (payload.message_id) {
        const nextId = String(payload.message_id);
        if (nextId !== assistantMessageId) {
          push(effects, {
            op: "assistantMessageId",
            messageId: nextId,
            previousMessageId: assistantMessageId,
          });
          assistantMessageId = nextId;
        }
      }
      const render = resolveCopilotStreamRender(payload);
      if (render) {
        push(effects, {
          op: "render",
          render,
          serverId: payload.message_id
            ? String(payload.message_id)
            : undefined,
        });
        if (
          render.type === "clarify" &&
          Array.isArray(render.options) &&
          render.options.length
        ) {
          push(effects, { op: "unbusyClarify" });
        }
      }
      return { effects };
    }

    if (type === "sources" && payload.sources) {
      push(effects, { op: "sources", sources: payload.sources });
      return { effects };
    }

    if (type === "degraded") {
      const degraded = normalizeCopilotDegradedPayload(payload);
      if (degraded.isReducedMode) {
        push(effects, {
          op: "degraded",
          isReducedMode: true,
          degradeReason: degraded.degradeReason,
          servicePath: degraded.servicePath,
        });
      }
      return { effects };
    }

    if (type === "complete") {
      if (payload.message_id) {
        const nextId = String(payload.message_id);
        if (nextId !== assistantMessageId) {
          push(effects, {
            op: "assistantMessageId",
            messageId: nextId,
            previousMessageId: assistantMessageId,
          });
          assistantMessageId = nextId;
        }
      }

      const data = payload.data || {};
      const finalContent = data.content;
      if (finalContent && finalContent !== content) {
        const wasEmpty = content.length === 0;
        content = finalContent;
        push(effects, {
          op: "content",
          content,
          clearTyping: wasEmpty,
        });
      }

      const completeMapped = mapCopilotStreamCompleteData(data);
      if (
        completeMapped.render ||
        completeMapped.sources ||
        completeMapped.suggestions
      ) {
        push(effects, {
          op: "render",
          render: completeMapped.render,
          sources: completeMapped.sources,
          suggestions: completeMapped.suggestions,
          serverId: payload.message_id
            ? String(payload.message_id)
            : undefined,
        });
      }

      const filters = mapCopilotStreamFilters(data);
      if (filters) {
        push(effects, {
          op: "filters",
          activeFilters: filters.activeFilters,
          contextSummary: filters.contextSummary,
        });
      }

      const degraded = normalizeCopilotDegradedPayload(data);
      if (degraded.isReducedMode) {
        push(effects, {
          op: "degraded",
          isReducedMode: true,
          degradeReason: degraded.degradeReason,
          servicePath: degraded.servicePath,
        });
      }

      push(effects, { op: "unbusyComplete" });
      return { effects };
    }

    if (type === "error") {
      const streamError = new Error(
        payload.message || "Something went wrong with Vertex360 Copilot."
      );
      streamError.code = payload.code || "AI_UNAVAILABLE";
      streamError.status = payload.status || 500;
      return { effects, error: streamError };
    }

    return { effects };
  }

  /**
   * Interpret the transport-level complete object returned by sendMessageStream
   * (saga ``done`` channel item). Mirrors ``complete`` event fields when present.
   * @param {object} complete
   */
  function handleTransportComplete(complete) {
    const payload = {
      type: "complete",
      conversation_id: complete?.conversation_id,
      message_id: complete?.message_id,
      data: complete?.data || complete,
    };
    // Prefer nested data when the promise resolves with a full complete event.
    if (complete?.type === "complete" && complete.data) {
      payload.data = complete.data;
      payload.conversation_id =
        complete.conversation_id || payload.conversation_id;
      payload.message_id = complete.message_id || payload.message_id;
    }
    return handleEvent(payload);
  }

  return {
    get content() {
      return content;
    },
    get assistantMessageId() {
      return assistantMessageId;
    },
    get conversationId() {
      return conversationId;
    },
    handleEvent,
    handleTransportComplete,
  };
}
